import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Circle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Sparkles,
  X,
} from "lucide-react";

const SESSION_KEY = "gt_onboarding_collapsed_v1";

/**
 * Sprint AJ — single source of truth for step completion, shared by the card
 * on MyEvents and the "Setup complete" sidebar chip. Keeps them in lockstep.
 */
export function computeOnboardingCompletion(user, events) {
  const list = Array.isArray(events) ? events : [];
  const hasBiz = !!(user?.business_name && user?.state);
  const hasEvent = list.length > 0;
  const hasTicketType = list.some((e) => (e.ticket_types_summary?.count || 0) >= 1);
  const hasPublishedPublic = list.some(
    (e) => e.status === "published" && e.visibility === "public",
  );
  const pd = user?.payout_details || {};
  const hasBank = !!(
    (pd.account_name || "").trim() &&
    (pd.bsb || "").trim() &&
    (pd.account_number || "").trim()
  );
  const hasShared = !!user?.event_shared_at;
  return {
    hasBiz,
    hasEvent,
    hasTicketType,
    hasPublishedPublic,
    hasBank,
    hasShared,
    allDone: hasBiz && hasEvent && hasTicketType && hasPublishedPublic && hasBank && hasShared,
  };
}

function StepRow({ done, num, title, ctaLabel, ctaTo, ctaAction, testid }) {
  return (
    <li
      className={`flex items-start gap-3 py-3 border-b border-[var(--gt-line)]/60 last:border-b-0 ${
        done ? "opacity-70" : ""
      }`}
      data-testid={testid}
      data-onboarding-status={done ? "complete" : "incomplete"}
    >
      <span className="mt-0.5 shrink-0" aria-hidden="true">
        {done ? (
          <CheckCircle2 size={20} className="text-emerald-600" />
        ) : (
          <Circle size={20} className="text-[var(--gt-ink-3)]" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="gt-label">Step {num}</span>
          {done && (
            <span
              className="text-[10px] uppercase tracking-widest font-bold text-emerald-700"
              data-testid={`${testid}-done-tag`}
            >
              Complete
            </span>
          )}
        </div>
        <div
          className={`text-sm font-semibold text-[var(--gt-ink)] ${
            done ? "line-through decoration-1 decoration-[var(--gt-ink-3)]" : ""
          }`}
        >
          {title}
        </div>
      </div>
      {!done && ctaLabel && (
        ctaTo ? (
          <Link
            to={ctaTo}
            className="gt-btn-ghost text-xs shrink-0"
            data-testid={`${testid}-cta`}
          >
            {ctaLabel} <ArrowRight size={12} />
          </Link>
        ) : (
          <button
            type="button"
            onClick={ctaAction}
            className="gt-btn-ghost text-xs shrink-0"
            data-testid={`${testid}-cta`}
          >
            {ctaLabel} <ArrowRight size={12} />
          </button>
        )
      )}
    </li>
  );
}

/**
 * Sprint AJ — Organiser onboarding checklist.
 *
 * Props:
 *   user     — the `/auth/me` payload
 *   events   — the `/events/mine` list (with ticket_types_summary)
 *   onShareCtaClick — called when the organiser wants to jump to the first
 *                     published/public event to trigger "Copy link"
 */
export default function OnboardingChecklist({ user, events, onShareCtaClick }) {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") setCollapsed(true);
    } catch {
      /* private mode / unavailable — ignore */
    }
  }, []);

  const dismiss = () => {
    setCollapsed(true);
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  const expand = () => {
    setCollapsed(false);
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
  };

  const steps = useMemo(() => {
    const { hasBiz, hasEvent, hasTicketType, hasPublishedPublic, hasBank, hasShared } =
      computeOnboardingCompletion(user, events);
    return [
      {
        num: 1,
        title: "Add your business name & state",
        done: hasBiz,
        ctaLabel: "Go to profile",
        ctaTo: "/dashboard/profile",
        testid: "onboarding-step-business",
      },
      {
        num: 2,
        title: "Create your first event",
        done: hasEvent,
        ctaLabel: "Create event",
        ctaTo: "/dashboard/events/new",
        testid: "onboarding-step-create-event",
      },
      {
        num: 3,
        title: "Add at least one ticket type",
        done: hasTicketType,
        ctaLabel: "Open event",
        ctaTo: hasEvent
          ? `/dashboard/events/${events[0].id}/edit`
          : "/dashboard/events/new",
        testid: "onboarding-step-ticket-type",
      },
      {
        num: 4,
        title: "Publish your event (public/buyer-visible)",
        done: hasPublishedPublic,
        ctaLabel: "Open event",
        ctaTo: hasEvent
          ? `/dashboard/events/${events[0].id}/edit`
          : "/dashboard/events/new",
        testid: "onboarding-step-publish",
      },
      {
        num: 5,
        title: hasBank ? "Bank details added" : "Add bank details for payouts",
        done: hasBank,
        ctaLabel: "Add bank details",
        ctaTo: "/dashboard/profile",
        testid: "onboarding-step-bank",
      },
      {
        num: 6,
        title: "Share your event link",
        done: hasShared,
        ctaLabel: hasPublishedPublic ? "Copy link" : "Publish first",
        ctaAction: hasPublishedPublic ? onShareCtaClick : undefined,
        ctaTo: hasPublishedPublic ? undefined : "/dashboard/events/new",
        testid: "onboarding-step-share",
      },
    ];
  }, [user, events, onShareCtaClick]);

  const doneCount = steps.filter((s) => s.done).length;
  const total = steps.length;
  const allDone = doneCount === total;

  if (allDone) return null; // auto-hide when everything's ticked

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={expand}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 mb-6 border border-[var(--gt-line)] bg-[var(--gt-bg-surface)] rounded-sm hover:border-[var(--gt-ink)] transition-colors"
        data-testid="onboarding-collapsed"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-[var(--gt-ink)]">
          <Sparkles size={14} className="text-[var(--gt-accent)]" />
          Setup: {doneCount} of {total} steps complete
        </span>
        <ChevronDown size={16} className="text-[var(--gt-ink-2)]" />
      </button>
    );
  }

  return (
    <div
      className="mb-8 border border-[var(--gt-line)] rounded-sm bg-white"
      data-testid="onboarding-checklist"
    >
      <div className="flex items-start justify-between gap-3 p-5 border-b border-[var(--gt-line)]">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-sm bg-[var(--gt-ink)] text-white flex items-center justify-center shrink-0">
            <Sparkles size={16} />
          </div>
          <div>
            <div className="gt-label mb-1">Get started</div>
            <h3 className="font-heading text-xl font-black tracking-tight leading-tight">
              Set up your organiser account
            </h3>
            <p className="text-xs text-[var(--gt-ink-2)] mt-1">
              {doneCount} of {total} steps complete — finish these to be ready
              to sell.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={dismiss}
            className="text-xs text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)] px-2 py-1 inline-flex items-center gap-1"
            data-testid="onboarding-dismiss-btn"
            aria-label="Collapse checklist for this session"
          >
            <ChevronUp size={14} /> I&apos;ll come back later
          </button>
        </div>
      </div>
      <ul className="px-5 py-1" data-testid="onboarding-steps">
        {steps.map((s) => (
          <StepRow
            key={s.testid}
            num={s.num}
            title={s.title}
            done={s.done}
            ctaLabel={s.ctaLabel}
            ctaTo={s.ctaTo}
            ctaAction={s.ctaAction}
            testid={s.testid}
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * Persistent "Setup complete ✓" sidebar chip.
 * Rendered only when every step is complete AND the organiser has not
 * dismissed the chip. Dismissal is per-organiser via localStorage keyed by
 * user.id (no backend persistence added — the checklist itself is derived
 * from real data on every load, so this chip is pure UI garnish).
 */
export function SetupCompleteChip({ user, events }) {
  const [dismissed, setDismissed] = useState(true); // default hidden until we check

  const localKey = user?.id ? `gt_setup_chip_dismissed_${user.id}` : null;

  const isAllDone = useMemo(
    () => (user && Array.isArray(events) ? computeOnboardingCompletion(user, events).allDone : false),
    [user, events],
  );

  useEffect(() => {
    if (!localKey) return;
    try {
      setDismissed(localStorage.getItem(localKey) === "1");
    } catch {
      setDismissed(false);
    }
  }, [localKey]);

  if (!isAllDone || dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    if (localKey) {
      try {
        localStorage.setItem(localKey, "1");
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <div
      className="mt-4 flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-emerald-500/40 bg-emerald-50 text-emerald-900"
      data-testid="setup-complete-chip"
    >
      <span className="inline-flex items-center gap-2 text-xs font-bold">
        <CheckCircle2 size={14} /> Setup complete
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        className="text-emerald-900/70 hover:text-emerald-900"
        aria-label="Dismiss setup-complete chip"
        data-testid="setup-complete-chip-dismiss"
      >
        <X size={12} />
      </button>
    </div>
  );
}
